import { ROSTER_NO_AREA, jobProfileLabel, personDisplayName, type Person, type ServicePlace } from '@vigeo/shared';

/** The filter value for "no service area recorded". Not a possible typed area: it starts with a character a person would not type. */
export const NO_AREA = ROSTER_NO_AREA;

/** One typed service area per person (ADR-016, #459): `Person.assignment.serviceArea`, trimmed, or null. */
export function serviceAreaOf(person: Person): string | null {
  const area = person.assignment?.serviceArea?.trim();
  return area === undefined || area === '' ? null : area;
}

/** The places on a person (ADR-018, ADR-021): place codes with a kind. Empty for a record saved before places existed. */
export function placesOf(person: Person): readonly ServicePlace[] {
  return person.assignment?.servicePlaces ?? [];
}

/** What the Roster says for a person with no job profile: the words the quick look and the profile use. */
export const NO_JOB_TITLE = 'No role set';

/**
 * A person's job title as one line (#798): their job profiles' labels, joined as the quick look joins them. The labels are the
 * shared catalog's own, read with no request, so the Table view does not read the catalog (#699). A key the catalog does not
 * hold reads as itself.
 */
export function jobTitleLine(person: Pick<Person, 'jobProfileKeys'>): string {
  if (person.jobProfileKeys.length === 0) return NO_JOB_TITLE;
  return person.jobProfileKeys.map((key) => jobProfileLabel(key) ?? key).join(' · ');
}

/** Whether a person shows no service area at all: no typed text and no place. The typed text shows only when there are no places. */
export const hasNoServiceArea = (person: Person): boolean => serviceAreaOf(person) === null && placesOf(person).length === 0;

const same = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0;

/**
 * The areas already in use, for the type-ahead and the filter. Spelling is free text, so two people
 * typed "north county" and "North County" are one area: the first spelling met wins, and the list is
 * sorted so it does not reshuffle as people are added.
 */
export function serviceAreasOf(people: readonly Person[]): string[] {
  const seen: string[] = [];
  for (const person of people) {
    const area = serviceAreaOf(person);
    if (area !== null && !seen.some((s) => same(s, area))) seen.push(area);
  }
  return seen.sort((a, b) => a.localeCompare(b));
}

/** `all`, `NO_AREA`, or an area (compared without regard to case). */
export function inServiceArea(person: Person, filter: string): boolean {
  if (filter === 'all') return true;
  const area = serviceAreaOf(person);
  if (filter === NO_AREA) return hasNoServiceArea(person);
  return area !== null && same(area, filter);
}

/**
 * Name, email, phone and service area: every word typed must appear in one of them. The words for a
 * person's places count too when `placeNameOf` can say them (the page gives the words from the app's own list of places).
 */
export function matchesStaffQuery(person: Person, query: string, placeNameOf?: (placeId: string) => string | undefined): boolean {
  const terms = query.trim().toLowerCase().split(/\s+/).filter((t) => t !== '');
  if (terms.length === 0) return true;
  const haystack = [
    personDisplayName(person.name), person.name.givenName, person.name.familyName,
    person.contact.email, person.contact.phone ?? '', serviceAreaOf(person) ?? '',
    ...placesOf(person).map((place) => placeNameOf?.(place.placeId) ?? ''),
  ].join(' ').toLowerCase();
  return terms.every((term) => haystack.includes(term));
}
